/**
 * THE 15-MINUTE UNDO-RELEASE WINDOW — the one arithmetic, in one place.
 *
 * Authority: the Technical Delegate's authorisation of 9 September 2026 to build the parked
 * "15-minute Undo Release" item. Until that authorisation `released` was terminal in every
 * direction — see `docs/specs/um-app/11-release-and-debrief.md` §2 and its dated amendment,
 * and migration 051, which is where the lattice actually moves.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THE WINDOW IS, IN ONE SENTENCE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A released note may be **unreleased by its author**, returning it to whatever it was
 * immediately before the release, for **15 minutes from the instant of that release** — and
 * after that release is final, exactly as it was before this feature existed.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SERVER IS THE AUTHORITY. THIS MODULE IS ARITHMETIC, NOT A GATE.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `undoWindowRemainingMs` is used on BOTH sides, and the two uses are not equal in standing:
 *
 *   * On the server it is computed against the DATABASE clock (`now()` inside the same
 *     statement that moves the row), and migration 051's trigger applies the same interval a
 *     second time on the row itself. A request that arrives late is refused there, whatever
 *     any client believes.
 *   * In the browser it draws a COUNTDOWN and nothing else. A phone at a hockey venue can be
 *     minutes out — the clock skew this codebase already refuses to trust for `capturedAt`
 *     (spec 02 §2.7) is the same skew here — so the client's number is advisory. A countdown
 *     that has not yet reached zero on a skewed phone will simply meet a typed 409 when it is
 *     pressed, which is the honest outcome and is the one the screen renders.
 *
 * There is deliberately no "grace" of any kind, on either side. A window that is 15 minutes on
 * the label and 15 minutes and a bit in the code is a window nobody can describe truthfully.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A DEADLINE IS DERIVED, NEVER STORED — `hearing-clocks.ts`'s rule, for its reason
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * There is no `undo_expires_at` column. There is `note.released_at`, there is the constant
 * below, and there is the addition. A stored deadline is a number that can drift from the
 * instant it was computed from, and `released_at` is the one fact the whole feature turns on.
 */
/**
 * FIFTEEN MINUTES, and the number is here rather than in a Configuration row.
 *
 * It is not a tournament setting: it is the length of "I have just this second pressed the
 * wrong button", and a TD who could widen it to a day would be a TD who could quietly make
 * release retractable — which is a different promise from the one the panel was given. If it
 * ever needs to move it moves here, in a release, with the spec amended in the same commit.
 */
export const UNDO_RELEASE_WINDOW_MINUTES = 15;
/** The same figure in milliseconds, for the two clocks that count in them. */
export const UNDO_RELEASE_WINDOW_MS = UNDO_RELEASE_WINDOW_MINUTES * 60 * 1000;
/**
 * The instant the undo stops being offered, as an ISO string — `released_at + 15 minutes`.
 *
 * Returns `null` for an unparseable stamp rather than throwing or guessing: a note whose
 * `released_at` cannot be read is a note whose window cannot be stated, and the caller must
 * render that as "no undo" rather than as a countdown from an invented moment.
 */
export function undoWindowExpiresAt(releasedAt) {
    const at = Date.parse(releasedAt);
    if (Number.isNaN(at))
        return null;
    return new Date(at + UNDO_RELEASE_WINDOW_MS).toISOString();
}
/**
 * Milliseconds left in the window, floored at zero. `0` means expired, and an unreadable or
 * absent stamp means expired too — the safe direction, since the only thing a caller does
 * with a positive number is OFFER an undo.
 */
export function undoWindowRemainingMs(releasedAt, now) {
    if (releasedAt === null)
        return 0;
    const at = Date.parse(releasedAt);
    if (Number.isNaN(at))
        return 0;
    return Math.max(0, at + UNDO_RELEASE_WINDOW_MS - now.getTime());
}
/** Whether the undo may still be OFFERED. The server decides whether it may be TAKEN. */
export function undoWindowIsOpen(releasedAt, now) {
    return undoWindowRemainingMs(releasedAt, now) > 0;
}
/**
 * `15 min`, `14 min`, … `1 min`, then `Less Than 1 Min` — the countdown as the screen says it.
 *
 * **Rounded UP to the whole minute, deliberately.** A window with 61 seconds left reads
 * `2 min`; a window with 59 reads `Less Than 1 Min`. Rounding down would show `0 min` beside a
 * button that still works, which reads as broken; rounding up never promises time that is not
 * there, because the last minute is named rather than counted.
 *
 * Returns `null` when the window has closed, so a caller cannot render a countdown for an undo
 * that is no longer on offer.
 */
export function undoWindowLabel(remainingMs) {
    if (remainingMs <= 0)
        return null;
    if (remainingMs < 60 * 1000)
        return "Less Than 1 Min";
    return `${String(Math.ceil(remainingMs / 60000))} Min`;
}
//# sourceMappingURL=undo-release.js.map